import React, { useState } from 'react';
import {
  isAppleConfigured,
  isGoogleConfigured,
  signInWithApple,
  signInWithGoogle,
} from '../../socialAuth';
import './SocialAuthButtons.css';

/**
 * Renders Google and Apple sign-in buttons on Login/Register pages.
 *
 * Props:
 *  - onSignedIn(result): called after a successful backend exchange.
 *  - onError(message): called on failures (including 409 email_in_use).
 *  - loginWithGoogle / loginWithApple: from AuthContext.
 *  - mode: 'login' | 'register' (cosmetic).
 */
const SocialAuthButtons = ({
  onSignedIn,
  onError,
  loginWithGoogle,
  loginWithApple,
  mode = 'login',
  hideDivider = false,
  dividerPosition = 'before',
}) => {
  const [busy, setBusy] = useState(null);

  const googleAvailable = isGoogleConfigured();
  const appleAvailable = isAppleConfigured();

  if (!googleAvailable && !appleAvailable) {
    return null;
  }

  const handleGoogle = async () => {
    setBusy('google');
    try {
      const { id_token } = await signInWithGoogle();
      const result = await loginWithGoogle(id_token);
      if (result.success) {
        onSignedIn?.(result, 'google');
      } else if (result.code === 'email_in_use') {
        onError?.(result.error);
      } else {
        onError?.(result.error || 'Google-login mislykkedes.');
      }
    } catch (err) {
      onError?.(err.message || 'Kunne ikke åbne Google-login.');
    } finally {
      setBusy(null);
    }
  };

  const handleApple = async () => {
    setBusy('apple');
    try {
      const { id_token, name } = await signInWithApple();
      const result = await loginWithApple(id_token, name);
      if (result.success) {
        onSignedIn?.(result, 'apple');
      } else if (result.code === 'email_in_use') {
        onError?.(result.error);
      } else {
        onError?.(result.error || 'Apple-login mislykkedes.');
      }
    } catch (err) {
      // Apple popup dismissals throw; surface a friendly message.
      if (err && (err.error === 'popup_closed_by_user' || err.error === 'user_cancelled_authorize')) {
        onError?.(null);
      } else {
        onError?.(err.message || 'Kunne ikke åbne Apple-login.');
      }
    } finally {
      setBusy(null);
    }
  };

  const label = mode === 'register' ? 'Tilmeld med' : 'Fortsæt med';
  const divider = !hideDivider ? (
    <div className="social-auth-divider"><span>eller</span></div>
  ) : null;

  return (
    <div className={`social-auth-buttons${hideDivider ? ' social-auth-buttons--flush' : ''}`}>
      {dividerPosition === 'after' ? null : divider}
      {googleAvailable && (
        <button
          type="button"
          className="social-auth-btn social-auth-btn--google"
          onClick={handleGoogle}
          disabled={busy !== null}
        >
          <span className="social-auth-icon" aria-hidden="true">
            <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
              <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" />
              <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" />
              <path fill="#FBBC05" d="M3.964 10.706A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.706V4.962H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.038l3.007-2.332z" />
              <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.962L3.964 7.294C4.672 5.167 6.656 3.58 9 3.58z" />
            </svg>
          </span>
          {busy === 'google' ? 'Forbinder...' : `${label} Google`}
        </button>
      )}
      {appleAvailable && (
        <button
          type="button"
          className="social-auth-btn social-auth-btn--apple"
          onClick={handleApple}
          disabled={busy !== null}
        >
          <span className="social-auth-icon" aria-hidden="true">
            <svg width="18" height="18" viewBox="0 0 384 512" xmlns="http://www.w3.org/2000/svg">
              <path fill="currentColor" d="M318.7 268.7c-.2-37 16.4-65 50-85.7-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 140.9 4 184.5 4 273.5q0 39.4 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zM260.7 81c12.5-14.9 22.4-32.5 22.4-50.6 0-2.5-.2-5-.7-7-22.1 1-48.9 14.9-65.1 33.3-12.5 14.2-23.7 31-23.7 49.6 0 2.7.5 5.5.7 6.4 1.4.2 2.9.4 4.3.4 19.8 0 44.7-13.2 61.1-32z" />
            </svg>
          </span>
          {busy === 'apple' ? 'Forbinder...' : `${label} Apple`}
        </button>
      )}
      {dividerPosition === 'after' ? divider : null}
    </div>
  );
};

export default SocialAuthButtons;
